/**
 * AppFooter — slim, copyright-only footer.
 *
 * Previously this footer rendered a 3-column block (Contact Us / Email
 * Support / Request a Call / Live Chat / Help & FAQs / Privacy & Security
 * / Medical Disclaimer) on every page that mounted it. That duplicated the
 * content already housed in the dedicated Support tab (/support-help) and
 * created an information-hierarchy problem: users saw the same support
 * blocks at the bottom of Services, Profile, Nutrition, Dashboard, etc.,
 * even though Support is its own bottom-nav destination.
 *
 * MVP launch-readiness restructuring:
 *   - All Contact / FAQ / Privacy / Disclaimer content has been moved
 *     exclusively into the Support tab.
 *   - This footer is now just the copyright row + the (existing) admin
 *     sign-in link, which other components and tests rely on by testid.
 *   - No backend changes, no route changes, no new dependencies.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';

const AppFooter = () => (
  <footer className="bg-white border-t border-gray-200 mt-auto" data-testid="app-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div className="flex flex-col items-center gap-1.5">
        <p
          className="text-[11px] uppercase tracking-wider font-medium text-emerald-700 text-center"
          data-testid="footer-tagline"
        >
          HealthLoop – A Digital Preventive Health Education Platform
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
          <p className="text-xs text-gray-600 text-center">
            © {new Date().getFullYear()} HealthLoop. Preventive health education and wellness engagement.
          </p>
          <span className="hidden sm:inline text-gray-300">·</span>
          <Link
            to="/admin/login"
            className="text-[11px] text-gray-500 hover:text-indigo-600 transition-colors inline-flex items-center gap-1"
            data-testid="footer-admin-signin-link"
          >
            <Shield className="h-3 w-3" />
            Admin sign-in
          </Link>
        </div>
        {/* Persistent regulatory line — visible on every page that mounts
            AppFooter. Cannot be dismissed. Communicates the four required
            points: educational scope, no diagnosis, no emergency care,
            and where to escalate when urgent. Kept compact so it does not
            crowd the rest of the footer. */}
        <p
          className="text-[10px] sm:text-[11px] text-gray-500 text-center max-w-2xl leading-relaxed mt-2 px-2"
          data-testid="footer-medical-disclaimer"
        >
          HealthLoop offers <strong className="font-semibold">preventive health education</strong> only.
          It is <strong className="font-semibold">not a diagnostic tool</strong> and{' '}
          <strong className="font-semibold">not an emergency medical service</strong>.
          If your situation is urgent, contact your local emergency number now
          — <span className="whitespace-nowrap">UK 999</span> · <span className="whitespace-nowrap">US 911</span> · <span className="whitespace-nowrap">EU 112</span>
          — or speak to a qualified healthcare provider.
        </p>
      </div>
    </div>
  </footer>
);

export default AppFooter;
